import React from 'react';
import { createContext, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MembershipLevel } from '../domain/models/MembershipLevel';
import { CurrentPageActionType, MemberAccountGroupData } from '../pages/ZClubAccount/ZClubAccountPageCommon';
import { useZerezesServices } from './ZerezesServicesProvider';
import { Reward } from '../domain/models/Reward';

interface IZerezesClubAccount {
  ztoken?: string | null;
  zstore?: string | null;
  loading?: boolean | null;
  currentPageAction?: string;
  membershipLevelList: MembershipLevel[];
  memberAccountGroupData?: MemberAccountGroupData | null;
  selectMemberAccount: (shopifyCustomerId?: number) => void;
  refresh: () => void;
  search: () => void;
  startExchangePointsReward: (reward: Reward) => void;
  pointsExchangeReward?: Reward | null;
  cancelExchangePointsReward: () => void;
  setZParams?: (ztoken?: string | null, zstore?: string | null, sislogUser?: string | null) => void;
  clearZParams?: () => void;
  sislogUser?: string | null;
}

const ZerezesClubAccountContext = createContext<IZerezesClubAccount>({
  membershipLevelList: [],
  selectMemberAccount: () => { },
  refresh: () => { },
  search: () => { },
  startExchangePointsReward: () => { },
  cancelExchangePointsReward: () => { },
});

const ZerezesClubAccountProvider = (props: any) => {

  const [searchParams] = useSearchParams();
  const [ztoken, setZToken] = React.useState<string | null | undefined>(searchParams.get('ztoken') || localStorage.getItem('ztoken'));
  const [zstore, setZStore] = React.useState<string | null | undefined>(searchParams.get('loja') || localStorage.getItem('zstore'));
  const [sislogUser, setSislogUser] = React.useState<string | null | undefined>(localStorage.getItem('sislog_user'));
  const [loading, setLoading] = React.useState(false);
  const [currentPageAction, setCurrentPageAction] = React.useState(CurrentPageActionType.Search);
  const [membershipLevelList, setMembershipLevelList] = React.useState<MembershipLevel[]>([]);
  const [memberAccountGroupData, setMemberAccountGroupData] = React.useState<MemberAccountGroupData | null>(null);
  const [pointsExchangeReward, setPointsExchangeReward] = React.useState<Reward | null>(null);

  const { children } = props;

  const {
    getMemberAccountByShopifyCustomerIdService,
    getMembershipLevelListService,
    getMemberAccountPointsHistoryByShopifyCustomerIdService,
    getMembershipLevelRewardListService,
    getCustomerCashbackDetailsService
  } = useZerezesServices();

  React.useEffect(() => {
    if (!ztoken || !zstore) {
      return;
    }

    if (membershipLevelList.length === 0) {
      getMembershipLevelListService?.handle(ztoken || '')?.then((result) => {
        setMembershipLevelList(result?.value?.list || []);
      })
    }
  }, [ztoken, zstore]);

  function refresh() {
    selectMemberAccount(memberAccountGroupData?.memberAccount?.shopifyCustomerId);
  }

  async function selectMemberAccount(shopifyCustomerId?: number) {
    setMemberAccountGroupData(null);

    if (!shopifyCustomerId) {
      return;
    }

    setLoading(true);

    const getMemberAccountByShopifyCustomerIdResult = await getMemberAccountByShopifyCustomerIdService?.handle(shopifyCustomerId, ztoken || '');
    const memberAccount = getMemberAccountByShopifyCustomerIdResult?.value?.memberAccount || null;

    if (!memberAccount) {
      setLoading(false);
      return;
    }

    const [
      membershipLevelRewardListResult,
      pointsHistoryResult,
      cashbackDetailsResult
    ] = await Promise.all([
      getMembershipLevelRewardListService?.handle(memberAccount?.membershipLevelId || 0, ztoken || ''),
      getMemberAccountPointsHistoryByShopifyCustomerIdService?.handle(shopifyCustomerId, memberAccount.id!, ztoken || ''),
      getCustomerCashbackDetailsService?.handle(shopifyCustomerId, ztoken || '')
    ]);

    setMemberAccountGroupData({
      memberAccount,
      membershipLevelRewardList: membershipLevelRewardListResult?.value?.rewards || [],
      pointsHistory: pointsHistoryResult?.value?.list || [],
      cashbackDetails: cashbackDetailsResult?.value?.couponCode ? {
        lastOrderDate: cashbackDetailsResult.value.lastOrderDate,
        ordersCount: cashbackDetailsResult.value.ordersCount,
        cashbackExpiresAt: cashbackDetailsResult.value.cashbackExpiresAt,
        formattedCashbackExpiresAt: cashbackDetailsResult.value.formattedCashbackExpiresAt,
        couponCode: cashbackDetailsResult.value.couponCode,
        discountValue: cashbackDetailsResult.value.discountValue
      } : null
    });

    setCurrentPageAction(CurrentPageActionType.Account);

    setLoading(false);
  }

  function search() {
    setCurrentPageAction(CurrentPageActionType.Search);
    //setMemberAccountGroupData(null);
  }

  function startExchangePointsReward(reward: Reward) {
    setPointsExchangeReward(reward);
    setCurrentPageAction(CurrentPageActionType.ExchangePoints);
  }

  function cancelExchangePointsReward() {
    setPointsExchangeReward(null);
    setCurrentPageAction(CurrentPageActionType.Account);
  }

  function setZParams(ztoken?: string | null, zstore?: string | null, sislogUser?: string | null) {
    localStorage.setItem('ztoken', ztoken || '');
    localStorage.setItem('zstore', zstore || '');
    localStorage.setItem('sislog_user', sislogUser || '');
    setZToken(ztoken);
    setZStore(zstore);
    setSislogUser(sislogUser);
  }

  function clearZParams() {
    localStorage.removeItem('ztoken');
    localStorage.removeItem('zstore');
    localStorage.removeItem('sislog_user');
    setZToken(null);
    setZStore(null);
    setSislogUser(null);
  }

  return (
    <ZerezesClubAccountContext.Provider value={{
      ztoken,
      zstore,
      loading,
      currentPageAction,
      membershipLevelList,
      memberAccountGroupData,
      selectMemberAccount,
      refresh,
      search,
      startExchangePointsReward,
      pointsExchangeReward,
      cancelExchangePointsReward,
      setZParams,
      clearZParams,
      sislogUser
    }}>
      {children}
    </ZerezesClubAccountContext.Provider>
  )
}

const useZerezesClubAccount = () => {
  const context = useContext(ZerezesClubAccountContext);
  return context;
}

export { ZerezesClubAccountProvider, useZerezesClubAccount };